/**
 * ViewFullReportButton — opens the full HTML session report in a new tab.
 *
 * Extracted so the "latest highlights card" on the Reports tab
 * (ParentDashboardPage.tsx) can offer a direct "View full report" CTA,
 * matching the tappable behaviour of the per-session rows below it.
 *
 * Reuses the popup-blocker-safe window.open pattern from
 * ParentSessionRow.tsx:73-120 verbatim:
 *   1. Open a blank _blank tab synchronously INSIDE the user gesture (pre-await).
 *   2. Sever the opener reference for security.
 *   3. Await the presigned URL from GET /sessions/{sessionId}/report-url.
 *   4. Assign the URL to the pre-opened tab.
 *   5. On any failure: close the blank tab and show an inline error (TO-LL-036).
 *
 * Enforces TO-LL-036 (fail-closed — report-open failures surface inline, never
 *   swallowed) and TO-LL-058 (absolute '@/' imports only).
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { getReportUrl } from '@/services/reports';

export interface ViewFullReportButtonProps {
  /** Session whose report should be opened. */
  sessionId: string;
  /**
   * first-run-guide (T12): optional button label, so the first-run guide can name the child
   * ("Read Sarah's report") without a second implementation of the report-opening flow.
   *
   * ⚠️ That flow is NOT a navigation. It pre-opens a tab inside the user gesture, then swaps in
   * a presigned URL fetched afterwards. Anyone tempted to "just navigate to the report" should
   * read the steps above first — the ordering is what survives popup blockers.
   */
  label?: string;
}

export function ViewFullReportButton({ sessionId, label }: ViewFullReportButtonProps) {
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const handleOpenReport = async () => {
    if (isLoadingReport) return;

    setIsLoadingReport(true);
    setReportError(null);

    // Step 1: Pre-open a blank tab synchronously BEFORE any await.
    // BUG-20260616-202933 pattern: do NOT pass 'noopener' — that returns null.
    const reportTab = window.open('', '_blank');

    // Step 2: If null, popup was blocked — surface an inline error and bail.
    if (reportTab === null) {
      setReportError('Please allow pop-ups to view the report.');
      setTimeout(() => setReportError(null), 3000);
      setIsLoadingReport(false);
      return;
    }

    // Step 3: Sever the opener reference — preserves no-opener security posture
    // without losing the tab handle.
    reportTab.opener = null;

    try {
      const { reportUrl } = await getReportUrl(sessionId);
      // Step 4: Assign the resolved URL to the pre-opened tab.
      try {
        reportTab.location.href = reportUrl;
      } catch {
        // Rare: user closed the blank tab before we could assign.
        reportTab.close();
        setReportError('Failed to open report. Please try again.');
        setTimeout(() => setReportError(null), 3000);
      }
    } catch (error: unknown) {
      // Step 5: Close the blank tab — no stray empty tab on failure (TO-LL-036).
      reportTab.close();
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to load report. Please try again.';
      setReportError(message);
      setTimeout(() => setReportError(null), 3000);
    } finally {
      setIsLoadingReport(false);
    }
  };

  return (
    <div className="mt-3">
      <Button
        variant="warm"
        size="sm"
        className="w-full"
        aria-label={label ?? 'View full report'}
        aria-busy={isLoadingReport}
        disabled={isLoadingReport}
        onClick={() => void handleOpenReport()}
      >
        {isLoadingReport ? 'Opening report…' : (label ?? 'View full report')}
      </Button>

      {/* Inline report error (TO-LL-036 — failures never swallowed) */}
      {reportError && (
        <p className="text-xs text-red-500 mt-1.5">{reportError}</p>
      )}
    </div>
  );
}
